// Keycloak OIDC — Authorization Code + PKCE
//
// Stratégie de config en deux étapes :
//   1. Au boot, on utilise les valeurs des variables VITE_KEYCLOAK_* (fallback)
//   2. En parallèle, on appelle GET /auth/keycloak/config sur le Gateway pour
//      récupérer les vraies URLs Keycloak (auth_url, token_url, client_id, realm).
//      Si le call réussit, on override `keycloakConfig` et on cache la réponse
//      en localStorage pour le prochain chargement.
//
// Ce double mécanisme garantit que :
//   - L'app boot toujours même si le Gateway est down (UX dégradée mais OK)
//   - Le backend peut faire évoluer realm/client_id/URLs sans redéploiement
//   - Le mismatch initial (sosMain vs sos-main) est impossible structurellement

import { env } from "../../../shared/constants/env";

const CACHE_KEY = "sosmain.keycloak_config";

type KeycloakRuntimeConfig = {
  authorizationEndpoint: string;
  tokenEndpoint:         string;
  revocationEndpoint:    string;
  userInfoEndpoint:      string;
  issuer:                string;
  clientId:              string;
  realm:                 string;
};

// Build à partir des valeurs d'env (utilisé au boot et en fallback).
function buildFromEnv(): KeycloakRuntimeConfig {
  const realm = env.keycloakRealm;
  const base  = env.keycloakUrl;
  return {
    issuer:                `${base}/realms/${realm}`,
    clientId:              env.keycloakClientId,
    realm,
    authorizationEndpoint: `${base}/realms/${realm}/protocol/openid-connect/auth`,
    tokenEndpoint:         `${base}/realms/${realm}/protocol/openid-connect/token`,
    revocationEndpoint:    `${base}/realms/${realm}/protocol/openid-connect/logout`,
    userInfoEndpoint:      `${base}/realms/${realm}/protocol/openid-connect/userinfo`,
  };
}

// ─── Config mutable (init au boot) ────────────────────────────────────────────
// On expose `keycloakConfig` comme un objet mutable qui pointe initialement
// vers les valeurs d'env, puis qui est mis à jour si le Gateway répond.
//
// Tous les consommateurs (keycloakAuthService, KeycloakWebViewScreen) lisent
// `keycloakConfig.authorizationEndpoint` etc. — ils profitent
// automatiquement de la valeur la plus récente.

const internalConfig = buildFromEnv();

export const keycloakConfig = {
  get issuer()                { return internalConfig.issuer; },
  get clientId()              { return internalConfig.clientId; },
  get authorizationEndpoint() { return internalConfig.authorizationEndpoint; },
  get tokenEndpoint()         { return internalConfig.tokenEndpoint; },
  get revocationEndpoint()    { return internalConfig.revocationEndpoint; },
  get userInfoEndpoint()      { return internalConfig.userInfoEndpoint; },

  // Le schéma applicatif `sosmain://auth/callback` devient une vraie URL.
  // Calculée depuis l'origine courante plutôt que codée en dur : la même build
  // fonctionne alors en local, en préproduction et en production.
  //
  // ⚠️ BLOQUANT, vérifié le 2026-08-13 sur auth.alomain.com / realm sos-main.
  // Le client reste `sos-mobile` (décision produit : la config Keycloak ne
  // change pas). Or ce client, EN L'ÉTAT, ne peut pas servir un navigateur :
  //
  //   • Valid redirect URIs = `sosmain://auth/callback` UNIQUEMENT.
  //     `http://localhost:7670/auth/callback` est rejeté en « Invalid
  //     parameter: redirect_uri ». Un navigateur ne suit pas un schéma
  //     applicatif, et rien ne lui renvoie le code d'autorisation.
  //
  //   • Le client est CONFIDENTIEL : un échange de code sans `client_secret`
  //     répond `unauthorized_client`. Une app JS ne peut pas détenir ce
  //     secret — il serait lisible dans le bundle par n'importe qui.
  //
  // Deux issues possibles, toutes deux hors de ce fichier :
  //   1. Ajouter les URIs web aux « Valid redirect URIs » + les « Web
  //      Origins », et rendre le client public (PKCE seul, sans secret) ;
  //   2. Ou garder le client confidentiel et faire échanger le code par le
  //      Gateway (BFF) : le secret reste côté serveur, le navigateur ne voit
  //      que le résultat. C'est aussi ce qui permettrait de sortir les tokens
  //      de sessionStorage (cf. shared/api/secureTokens.ts).
  get redirectUri() {
    return `${window.location.origin}/auth/callback`;
  },

  // `offline_access` est conservé mais son intérêt chute sur web : le refresh
  // token vit en sessionStorage et meurt avec l'onglet (cf. api/secureTokens).
  // À retirer si Keycloak est configuré pour refuser ce scope aux clients
  // publics — ce qui serait un durcissement légitime.
  scopes: ["openid", "profile", "email", "offline_access"],
};

// ─── Fetch du Gateway + cache ─────────────────────────────────────────────────

// Format de réponse documenté dans openapi.json (#/paths/auth/keycloak/config/get)
type GatewayKeycloakConfigResponse = {
  auth_url:   string;
  token_url:  string;
  client_id:  string;
  realm:      string;
};

function deriveEndpointsFromAuthUrl(
  res: GatewayKeycloakConfigResponse,
): KeycloakRuntimeConfig {
  // Le Gateway retourne `auth_url` et `token_url`. On dérive les autres
  // endpoints en remplaçant le suffixe — c'est l'approche standard OIDC
  // (toujours `/realms/{realm}/protocol/openid-connect/{action}`).
  const base = res.auth_url.replace(/\/protocol\/openid-connect\/auth$/, "");
  return {
    issuer:                base,
    clientId:              res.client_id,
    realm:                 res.realm,
    authorizationEndpoint: res.auth_url,
    tokenEndpoint:         res.token_url,
    revocationEndpoint:    `${base}/protocol/openid-connect/logout`,
    userInfoEndpoint:      `${base}/protocol/openid-connect/userinfo`,
  };
}

function applyRuntimeConfig(cfg: KeycloakRuntimeConfig): void {
  internalConfig.issuer                = cfg.issuer;
  internalConfig.clientId              = cfg.clientId;
  internalConfig.realm                 = cfg.realm;
  internalConfig.authorizationEndpoint = cfg.authorizationEndpoint;
  internalConfig.tokenEndpoint         = cfg.tokenEndpoint;
  internalConfig.revocationEndpoint    = cfg.revocationEndpoint;
  internalConfig.userInfoEndpoint      = cfg.userInfoEndpoint;
}

// Init au boot — non bloquant. Appelle GET /auth/keycloak/config sur le
// Gateway, applique le résultat si succès. En parallèle, hydrate depuis le
// cache localStorage pour avoir une bonne valeur même offline.
export async function initKeycloakConfig(): Promise<void> {
  // 1. Hydrate depuis le cache (évite un round-trip si on a déjà vu cette
  //    config au chargement précédent). localStorage étant synchrone, cette
  //    étape est désormais instantanée — elle l'était déjà en pratique.
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) {
      const cached = JSON.parse(raw) as KeycloakRuntimeConfig;
      applyRuntimeConfig(cached);
    }
  } catch { /* cache corrompu — on ignore */ }

  // 2. Fetch les valeurs fraîches du Gateway en parallèle. Fire-and-forget :
  //    si le réseau est down ou le Gateway répond mal, on garde le fallback.
  //
  //    On passe par `apiClient` (et non `fetch` brut) pour bénéficier de
  //    l'intercepteur qui déballe l'enveloppe `{ success, data, metadata }`
  //    introduite par openapi v2 : avec `fetch`, `json.auth_url` était
  //    `undefined` (les champs vivent sous `data`) et l'app retombait
  //    SILENCIEUSEMENT sur le fallback env à chaque boot.
  //    Import dynamique : `client.ts` dépend indirectement de ce module
  //    (client → keycloakAuthService → keycloakConfig), un import statique
  //    créerait un cycle au chargement.
  try {
    const { apiClient } = await import("../../../shared/api/client");
    const { data: json } = await apiClient.get<GatewayKeycloakConfigResponse>(
      "/auth/keycloak/config",
      { timeout: 5_000 },
    );

    if (!json?.auth_url || !json.token_url || !json.client_id || !json.realm) {
      if (env.isDev) console.warn("[keycloak] /config response missing fields, fallback to env");
      return;
    }

    const fresh = deriveEndpointsFromAuthUrl(json);
    applyRuntimeConfig(fresh);
    // Persiste pour le prochain chargement. `setItem` est synchrone et renvoie
    // void — pas de `.catch()` possible ici, contrairement à AsyncStorage. Il
    // peut en revanche JETER (quota dépassé, stockage bloqué), d'où le try.
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(fresh));
    } catch {
      /* cache best effort — la config fraîche est déjà appliquée en mémoire */
    }

    if (env.isDev) console.log("[keycloak] config hydratée depuis Gateway:", fresh.realm, fresh.clientId);
  } catch (e) {
    // Réseau down, Gateway down, timeout → on garde le fallback env.
    if (env.isDev) console.warn("[keycloak] /config fetch failed, using env fallback:", e);
  }
}

// ─── Helpers existants — inchangés ────────────────────────────────────────────

import type { AppRole } from "../../../shared/stores/sessionStore";

export function extractRoleFromToken(decodedToken: Record<string, unknown>): AppRole {
  const realmRoles = (decodedToken?.realm_access as { roles?: string[] })?.roles ?? [];
  const normalized = realmRoles.map((r) => r.toUpperCase());
  if (normalized.includes("ADMIN"))      return "ADMIN";
  if (normalized.includes("SUPERVISOR")) return "SUPERVISOR";
  if (normalized.includes("AGENT"))      return "AGENT";
  return "CLIENT";
}

export function decodeJwtPayload(token: string): Record<string, unknown> {
  try {
    const payload = token.split(".")[1];
    const binary  = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    // `atob` renvoie une chaîne binaire (Latin-1). On la réinterprète en UTF-8,
    // sinon les accents sont corrompus ("Jérémie" → "JÃ©rÃ©mie").
    const json = decodeURIComponent(
      Array.from(binary, (c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0")).join(""),
    );
    return JSON.parse(json);
  } catch {
    return {};
  }
}

// ─── Constantes de gestion du cycle de vie des tokens ────────────────────────
export const TOKEN_CONFIG = {
  EXPIRATION_BUFFER:    10 * 60 * 1000,
  PREEMPTIVE_REFRESH:   15 * 60 * 1000,
  WATCH_INTERVAL:       60 * 1000,
  REVOKE_TIMEOUT:       5_000,
  REFRESH_MAX_RETRIES:  2,
  REVOKE_MAX_RETRIES:   2,
  REFRESH_TOKEN_BUFFER: 5 * 60 * 1000,
} as const;
